import React, { useEffect, useState } from 'react';
import Sidebar from './Sidebar';
import { useAuth } from '../context/AuthContext';

const initialForm = {
  username: '',
  password: '',
  confirmPassword: '',
  email: '',
  nama_lengkap: '',
  role: 'EndUser'
};

const KelolaAkun = ({ activeTabProp }) => {
  const { user } = useAuth();

  const [form, setForm] = useState(initialForm);
  const [users, setUsers] = useState([]);
  const [editingId, setEditingId] = useState(null);

  const [status, setStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const isEditMode = editingId !== null;

  const fetchUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const response = await fetch(`/api/auth/users`);
      const result = await response.json();
      if (response.ok && result.success) {
        setUsers(result.users || []);
      } else {
        setStatus(`✗ ${result.message || 'Gagal mengambil daftar akun.'}`);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setStatus('✗ Gagal menghubungi server saat mengambil daftar akun.');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setStatus('');
  };

  const resetForm = () => {
    setForm(initialForm);
    setEditingId(null);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setStatus('');
  };

  const handleEdit = (selectedUser) => {
    setEditingId(selectedUser.id_user);
    setForm({
      username: selectedUser.username || '',
      password: '',
      confirmPassword: '',
      email: selectedUser.email || '',
      nama_lengkap: selectedUser.nama_lengkap || '',
      role: selectedUser.role || 'EndUser'
    });
    setStatus('Mode edit akun. Kosongkan password jika tidak ingin mengganti password.');
  };

  const validateForm = () => {
    if (!form.username || !form.email || !form.nama_lengkap || !form.role) {
      return 'Username, email, nama lengkap, dan role wajib diisi.';
    }
    if (!isEditMode && !form.password) {
      return 'Password wajib diisi untuk akun baru.';
    }
    if (form.password || form.confirmPassword) {
      if (form.password !== form.confirmPassword) {
        return 'Password dan konfirmasi password tidak sama.';
      }
      if (form.password.length < 6) {
        return 'Password minimal 6 karakter.';
      }
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (user?.role !== 'Admin') {
      setStatus('✗ Hanya Admin yang dapat mengelola akun.');
      return;
    }

    const validationMessage = validateForm();
    if (validationMessage) {
      setStatus(`✗ ${validationMessage}`);
      return;
    }

    setIsSubmitting(true);
    setStatus(isEditMode ? 'Memperbarui akun...' : 'Mendaftarkan akun...');

    try {
      const url = isEditMode
        ? `/api/auth/users/${editingId}`
        : `/api/auth/register`;

      const method = isEditMode ? 'PUT' : 'POST';

      const payload = {
        username: form.username,
        email: form.email,
        nama_lengkap: form.nama_lengkap,
        role: form.role
      };

      if (form.password) {
        payload.password = form.password;
      }

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setStatus(`✓ ${result.message || (isEditMode ? 'Akun berhasil diperbarui.' : 'Akun berhasil dibuat.')}`);
        resetForm();
        await fetchUsers();
      } else {
        setStatus(`✗ ${result.message || result.error || 'Gagal menyimpan akun.'}`);
      }
    } catch (error) {
      console.error('Error saving account:', error);
      setStatus('✗ Gagal menghubungi server.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async (selectedUser) => {
    if (user?.role !== 'Admin') {
      setStatus('✗ Hanya Admin yang dapat mengubah status akun.');
      return;
    }

    const nextStatus = !Boolean(selectedUser.is_active);
    const confirmMessage = nextStatus
      ? `Aktifkan akun "${selectedUser.username}"?`
      : `Nonaktifkan akun "${selectedUser.username}"?`;

    if (!window.confirm(confirmMessage)) return;

    try {
      const response = await fetch(`/api/auth/users/${selectedUser.id_user}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: nextStatus })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setStatus(`✓ ${result.message}`);
        await fetchUsers();
      } else {
        setStatus(`✗ ${result.message || 'Gagal mengubah status akun.'}`);
      }
    } catch (error) {
      console.error('Error updating account status:', error);
      setStatus('✗ Gagal menghubungi server.');
    }
  };

  const roleBadgeStyle = (role) => {
    const colors = {
      Admin: { backgroundColor: '#ffebee', color: '#c62828' },
      Analis: { backgroundColor: '#e3f2fd', color: '#1565c0' },
      EndUser: { backgroundColor: '#e8f5e9', color: '#2e7d32' }
    };
    return {
      display: 'inline-block',
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '0.85rem',
      fontWeight: '600',
      ...(colors[role] || { backgroundColor: '#eeeeee', color: '#333' })
    };
  };

  const inputStyle = {
    width: '100%',
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid #ddd'
  };

  return (
    <div className="dashboard-container">
      <Sidebar activeTabProp={activeTabProp || 'akun'}>
        <div className="dashboard-content">
          <div className="data-explorer">
            <h1>Kelola Akun</h1>

            {user?.role !== 'Admin' ? (
              <div className="input-error-message">
                Hanya Admin yang dapat mengakses fitur Kelola Akun.
              </div>
            ) : (
              <>
                <div className="upload-section">
                  <h2>{isEditMode ? 'Edit Akun' : 'Buat Akun Baru'}</h2>

                  <div className="upload-container" style={{ maxWidth: '700px' }}>
                    <form onSubmit={handleSubmit}>
                      <div style={{ display: 'grid', gap: '15px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                            Nama Lengkap <span style={{ color: 'red' }}>*</span>
                          </label>
                          <input type="text" value={form.nama_lengkap} onChange={(e) => handleChange('nama_lengkap', e.target.value)} placeholder="Contoh: Gede Karya" style={inputStyle} />
                        </div>

                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                            Username <span style={{ color: 'red' }}>*</span>
                          </label>
                          <input type="text" value={form.username} onChange={(e) => handleChange('username', e.target.value)} placeholder="Contoh: admin_jarrdin" style={inputStyle} />
                        </div>

                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                            Email <span style={{ color: 'red' }}>*</span>
                          </label>
                          <input type="email" value={form.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="Contoh: user@email.com" style={inputStyle} />
                        </div>

                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                            Role <span style={{ color: 'red' }}>*</span>
                          </label>
                          <select value={form.role} onChange={(e) => handleChange('role', e.target.value)} style={inputStyle}>
                            <option value="Admin">Admin</option>
                            <option value="Analis">Analis</option>
                            <option value="EndUser">EndUser</option>
                          </select>
                        </div>

                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                            Password {!isEditMode && <span style={{ color: 'red' }}>*</span>}
                          </label>
                          {isEditMode && (
                            <p style={{ margin: '0 0 8px 0', fontSize: '0.85rem', color: '#666' }}>
                              Kosongkan password jika tidak ingin mengganti password.
                            </p>
                          )}
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <input
                              type={showPassword ? 'text' : 'password'}
                              value={form.password}
                              onChange={(e) => handleChange('password', e.target.value)}
                              placeholder={isEditMode ? 'Password baru opsional' : 'Minimal 6 karakter'}
                              style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                            />
                            <button type="button" onClick={() => setShowPassword(prev => !prev)} style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ddd', backgroundColor: '#f5f5f5', cursor: 'pointer' }}>
                              {showPassword ? 'Sembunyikan' : 'Lihat'}
                            </button>
                          </div>
                        </div>

                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                            Konfirmasi Password {!isEditMode && <span style={{ color: 'red' }}>*</span>}
                          </label>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <input
                              type={showConfirmPassword ? 'text' : 'password'}
                              value={form.confirmPassword}
                              onChange={(e) => handleChange('confirmPassword', e.target.value)}
                              placeholder={isEditMode ? 'Ulangi password baru jika diisi' : 'Ulangi password'}
                              style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                            />
                            <button type="button" onClick={() => setShowConfirmPassword(prev => !prev)} style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ddd', backgroundColor: '#f5f5f5', cursor: 'pointer' }}>
                              {showConfirmPassword ? 'Sembunyikan' : 'Lihat'}
                            </button>
                          </div>
                        </div>

                        <div style={{ display: 'flex', gap: '10px' }}>
                          <button
                            type="submit"
                            disabled={isSubmitting}
                            style={{
                              flex: 1, padding: '10px',
                              backgroundColor: isEditMode ? '#2196F3' : '#4CAF50',
                              color: 'white', border: 'none', borderRadius: '4px',
                              cursor: isSubmitting ? 'not-allowed' : 'pointer',
                              opacity: isSubmitting ? 0.6 : 1, fontWeight: '600'
                            }}
                          >
                            {isSubmitting ? 'Menyimpan...' : isEditMode ? 'Update Akun' : 'Buat Akun'}
                          </button>

                          {isEditMode && (
                            <button
                              type="button"
                              onClick={resetForm}
                              disabled={isSubmitting}
                              style={{
                                padding: '10px 16px', backgroundColor: '#9e9e9e',
                                color: 'white', border: 'none', borderRadius: '4px',
                                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                opacity: isSubmitting ? 0.6 : 1, fontWeight: '600'
                              }}
                            >
                              Batal
                            </button>
                          )}
                        </div>

                        {status && (
                          <div className={status.includes('✓') ? 'save-success-message' : 'input-error-message'}>
                            {status}
                          </div>
                        )}
                      </div>
                    </form>
                  </div>
                </div>

                <div className="upload-section" style={{ marginTop: '30px' }}>
                  <h2>Daftar Akun</h2>
                  <div className="upload-container">
                    {isLoadingUsers ? (
                      <div style={{ padding: '20px', textAlign: 'center' }}>
                        <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
                        <p>Memuat daftar akun...</p>
                      </div>
                    ) : users.length === 0 ? (
                      <p style={{ color: '#666', fontStyle: 'italic' }}>Belum ada akun.</p>
                    ) : (
                      <div className="data-table-container">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Nama Lengkap</th>
                              <th>Username</th>
                              <th>Email</th>
                              <th>Role</th>
                              <th>Status</th>
                              <th>Dibuat</th>
                              <th style={{ width: '180px', textAlign: 'center' }}>Aksi</th>
                            </tr>
                          </thead>
                          <tbody>
                            {users.map((account) => (
                              <tr key={account.id_user}>
                                <td>{account.nama_lengkap}</td>
                                <td>{account.username}</td>
                                <td>{account.email}</td>
                                <td><span style={roleBadgeStyle(account.role)}>{account.role}</span></td>
                                <td>
                                  <span style={{
                                    display: 'inline-block', padding: '4px 8px', borderRadius: '4px',
                                    fontSize: '0.85rem', fontWeight: '600',
                                    backgroundColor: account.is_active ? '#e8f5e9' : '#ffebee',
                                    color: account.is_active ? '#2e7d32' : '#c62828'
                                  }}>
                                    {account.is_active ? 'Aktif' : 'Nonaktif'}
                                  </span>
                                </td>
                                <td>{account.created_at ? new Date(account.created_at).toLocaleString('id-ID') : '-'}</td>
                                <td style={{ textAlign: 'center' }}>
                                  <button
                                    type="button"
                                    onClick={() => handleEdit(account)}
                                    style={{ padding: '6px 10px', marginRight: '6px', backgroundColor: '#2196F3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleToggleStatus(account)}
                                    style={{ padding: '6px 10px', backgroundColor: account.is_active ? '#f44336' : '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
                                  >
                                    {account.is_active ? 'Nonaktifkan' : 'Aktifkan'}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </Sidebar>
    </div>
  );
};

export default KelolaAkun;